import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext, useSearch } from '@tanstack/react-router';
import { Button, Dropdown, Empty, Flex, message, Modal, Result, Tag, Typography } from 'antd';
import { isAxiosError } from 'axios';
import { ArrowLeft, Ellipsis, Eraser, Plus, RotateCw, Star, Unplug } from 'lucide-react';
import { useCallback, useState } from 'react';
import { z } from 'zod';

import {
  getListConnectionsQueryKey,
  useForceSyncConnection,
  useListConnections,
  useSetDefaultConnection,
} from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import { ClearConnectionDataModal } from '../../../../modules/connection/components/clear-connection-data-modal';
import { DisconnectConnectionModal } from '../../../../modules/connection/components/disconnect-connection-modal';
import { SignatureEditor } from '../../../../modules/connection/components/signature-editor';
import { useOAuthConnectFlow } from '../../../../modules/connection/hooks';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { Record } from '../../../../ui/components/record';
import { GoogleIcon } from '../../../../ui/icons/google';
import { MicrosoftIcon } from '../../../../ui/icons/microsoft';
import { formatDate } from '../../../../ui/utils/format-date';

import type { ListConnectionsResponseDtoItemsItem } from '../../../../api/generated/model';

const CONNECTED_APPS_REDIRECT_URI = `${window.location.origin}/settings/connected-apps`;

const SettingsConnectedAppsRoute = () => {
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_authenticated/_app/settings/connected-apps' });

  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const { can } = usePermissions();
  const organizationId = selectedOrganization.id;
  const canManageConnections = can('settings', 'view');

  const { data: connectionsData, error, status, refetch } = useListConnections(organizationId);
  const connections = connectionsData?.data?.items;

  const [clearModalConnectionId, setClearModalConnectionId] = useState<string | null>(null);
  const [disconnectModalConnectionId, setDisconnectModalConnectionId] = useState<string | null>(
    null,
  );

  const { connectGoogle, connectMicrosoft, isAuthorizing } = useOAuthConnectFlow({
    organizationId,
    redirectUri: CONNECTED_APPS_REDIRECT_URI,
    code: search.code,
    state: search.state,
  });

  const { mutate: forceSync } = useForceSyncConnection({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListConnectionsQueryKey(organizationId),
        });
        message.success('Sync started');
      },
      onError: (error) => {
        if (isAxiosError(error) && error.response?.status === 409) {
          message.warning('Sync is already running');
        } else {
          message.error('Failed to start sync. Please try again.');
        }
      },
    },
  });

  const { mutate: setDefaultConnection } = useSetDefaultConnection({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListConnectionsQueryKey(organizationId),
        });
        message.success('Default mailbox updated');
      },
      onError: () => message.error('Could not update default mailbox'),
    },
  });

  const handleForceSync = useCallback(
    (connectionId: string) => {
      Modal.confirm({
        title: 'Force Sync',
        content: 'Re-fetch the entire history from your provider. This may take a while.',
        okText: 'Start sync',
        cancelText: 'Cancel',
        onOk: () => {
          forceSync({ organizationId, connectionId });
        },
      });
    },
    [forceSync, organizationId],
  );

  const renderConnectionRow = (connection: ListConnectionsResponseDtoItemsItem) => {
    const {
      id,
      provider,
      providerAccountEmail,
      createdAt,
      emailSyncStatus,
      calendarSyncStatus,
      emailCount,
      calendarEventCount,
      isDefault,
      status: connectionStatus,
    } = connection;

    const needsReconnect = connectionStatus === 'disconnected';
    const isSyncInProgress =
      emailSyncStatus === 'backfilling' || calendarSyncStatus === 'backfilling';

    const description = needsReconnect ? (
      <Typography.Text type="warning">
        Sign-in expired · emails and calendar stopped syncing
      </Typography.Text>
    ) : (
      `Connected ${formatDate(createdAt)} · ${isSyncInProgress ? `Sync in progress…` : `${emailCount} emails · ${calendarEventCount} events`}`
    );

    return {
      key: id,
      style: needsReconnect ? { background: 'var(--ant-color-warning-bg)' } : undefined,
      reactNode: (
        <Record
          left={
            provider === 'google' ? (
              <GoogleIcon width={24} height={24} />
            ) : (
              <MicrosoftIcon width={24} height={24} />
            )
          }
          title={
            <Flex align="center" gap={8}>
              <span>{providerAccountEmail}</span>
              {isDefault && <Tag>Default</Tag>}
            </Flex>
          }
          description={description}
          right={
            canManageConnections ? (
              <Flex align="center" gap={8}>
                {needsReconnect && (
                  <Button
                    size="small"
                    loading={isAuthorizing}
                    onClick={() => (provider === 'google' ? connectGoogle() : connectMicrosoft())}
                  >
                    Reconnect
                  </Button>
                )}
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{
                    items: [
                      ...(!isDefault
                        ? [
                            {
                              key: 'set-default',
                              icon: <Star size="1em" />,
                              label: 'Set as default',
                              onClick: () =>
                                setDefaultConnection({ organizationId, connectionId: id }),
                            },
                          ]
                        : []),
                      {
                        key: 'force-sync',
                        icon: <RotateCw size="1em" />,
                        label: isSyncInProgress ? 'Sync in progress…' : 'Force Sync',
                        disabled: isSyncInProgress || needsReconnect,
                        onClick: () => handleForceSync(id),
                      },
                      { type: 'divider' },
                      {
                        key: 'clear',
                        icon: <Eraser size="1em" />,
                        label: 'Clear data',
                        onClick: () => setClearModalConnectionId(id),
                      },
                      {
                        key: 'disconnect',
                        icon: <Unplug size="1em" />,
                        label: 'Disconnect',
                        danger: true,
                        onClick: () => setDisconnectModalConnectionId(id),
                      },
                    ],
                  }}
                >
                  <Button type="text" icon={<Ellipsis size="1em" />} />
                </Dropdown>
              </Flex>
            ) : null
          }
        />
      ),
    };
  };

  const content = {
    pending: <Loader />,
    error:
      isAxiosError(error) && error.response?.status === 403 ? (
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view or edit this page. Please contact your administrator."
          extra={
            <Button icon={<ArrowLeft size="1em" />} onClick={() => window.history.back()}>
              Go back
            </Button>
          }
        />
      ) : (
        <Result
          status="error"
          title="Failed to load!"
          subTitle="Please try again. If this keeps happening, check your connection or contact support."
          extra={
            <Button icon={<RotateCw size="1em" />} onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      ),
    success: !connections?.length ? (
      <Empty description="No connected apps" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    ) : (
      <Flex vertical gap={24}>
        <List items={connections} renderItem={renderConnectionRow} />
        {canManageConnections && (
          <SignatureEditor organizationId={organizationId} connections={connections} />
        )}
      </Flex>
    ),
  };

  return (
    <SettingsLayoutContent
      title="Connected apps"
      description="Manage applications connected to your workspace."
      action={
        canManageConnections ? (
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'google',
                  icon: <GoogleIcon />,
                  label: 'Connect Google',
                  onClick: () => {
                    connectGoogle();
                  },
                },
                {
                  key: 'microsoft',
                  icon: <MicrosoftIcon />,
                  label: 'Connect Microsoft',
                  onClick: () => {
                    connectMicrosoft();
                  },
                },
              ],
            }}
          >
            <Button
              data-testid="connection-add"
              icon={<Plus size="1em" />}
              color="default"
              variant="filled"
              size="large"
              loading={isAuthorizing}
            />
          </Dropdown>
        ) : undefined
      }
    >
      {content[status]}

      {clearModalConnectionId && (
        <ClearConnectionDataModal
          organizationId={organizationId}
          connectionId={clearModalConnectionId}
          open
          onClose={() => setClearModalConnectionId(null)}
        />
      )}

      {disconnectModalConnectionId && (
        <DisconnectConnectionModal
          organizationId={organizationId}
          connectionId={disconnectModalConnectionId}
          open
          onClose={() => setDisconnectModalConnectionId(null)}
        />
      )}
    </SettingsLayoutContent>
  );
};

const SettingsConnectedAppsRouteSearchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/_app/settings/connected-apps')({
  component: SettingsConnectedAppsRoute,
  validateSearch: (params) => SettingsConnectedAppsRouteSearchSchema.parse(params),
});
